// TrackFiji V3.3.0 — Admin → Logs: is a tracker reaching the platform?
// V3.10.1: the message log (find a device + log) is at the top; the health table follows.
// Health table for every device (Traccar connection vs what the platform received, with a plain-English diagnosis).
// Below: the message log for one device (or unknown trackers), newest first, kept for 14 days.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { adminApi } from "../../lib/admin";
import { Notice, Toolbar } from "./ui";

type Diag = { level: "ok" | "warn" | "bad" | "idle"; code: string; text: string };
type DevRow = {
  id: string; name: string; unique_id: string; org: string | null; status: string;
  traccar: { id: number; status: string | null; last_update: string | null; last_fix: string | null; valid: boolean | null; protocol: string | null; disabled: boolean } | null;
  platform: { last_message: string | null; last_result: string | null; last_fix: string | null; quality: string | null; messages_24h: number };
  diagnosis: Diag;
};
type Unmatched = { unique_id: string; traccar_id: number | null; hits: number; last_seen: string };
type LogRow = { id: number; at: string; kind: string; result: string; fix_at: string | null; lat: number | null; lng: number | null;
  speed_kmh: number | null; valid: boolean | null; sat: number | null; protocol: string | null; detail: string | null; unique_id: string | null; attrs: Record<string, unknown> | null };

const fiji = (iso: string | null | undefined, sec = false) => iso ? new Intl.DateTimeFormat("en-FJ", { timeZone: "Pacific/Fiji", day: "2-digit", month: "short",
  hour: "2-digit", minute: "2-digit", ...(sec ? { second: "2-digit" } : {}), hour12: false }).format(new Date(iso)) : "—";
const ago = (iso: string | null | undefined, now: number) => {
  if (!iso) return "never";
  const m = (now - Date.parse(iso)) / 60000;
  return m < 1 ? "just now" : m < 90 ? `${Math.round(m)} min ago` : m < 48 * 60 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} days ago`;
};
const RESULT_TONE: Record<string, string> = { accepted: "ok", low: "warn", held: "warn", suspect: "warn", no_fix: "bad", unmatched: "bad", error: "bad" };
const RESULT_LABEL: Record<string, string> = { accepted: "Accepted", low: "Weak fix", held: "Held", suspect: "Held", no_fix: "No GPS fix", unmatched: "Unknown tracker", error: "Error" };
const LEVEL_LABEL: Record<Diag["level"], string> = { ok: "OK", warn: "Check", bad: "Problem", idle: "Inactive" };

export default function Logs() {
  const [diag, setDiag] = useState<{ at: string; devices: DevRow[]; unmatched: Unmatched[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // V3.12.0: notifications open Logs with ?device=<id> or ?unknown=<unique id> already picked
  const [pick, setPick] = useState<string>(() => {
    const q = new URLSearchParams(location.search);
    return q.get("device") ?? (q.get("unknown") ? `unmatched:${q.get("unknown")}` : "");
  });                                                       // device id, or "unmatched:<unique_id>"
  const [rows, setRows] = useState<LogRow[]>([]);
  const [result, setResult] = useState("");
  const [auto, setAuto] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const [q, setQ] = useState("");
  const [devQ, setDevQ] = useState("");                    // V3.10.1: find a device for the message log (top of page)
  const [company, setCompany] = useState("");              // V3.10.2: filter devices by company (log picker + health table)
  const logRef = useRef<HTMLElement>(null);
  const now = Date.now();

  const loadDiag = useCallback(async () => {
    setBusy(true); setErr(null);
    try { setDiag(await adminApi("GET", "diagnostics")); } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }, []);
  useEffect(() => { loadDiag(); }, [loadDiag]);

  const loadLog = useCallback(async () => {
    if (!pick) { setRows([]); return; }
    let qy = supabase.from("device_log").select("*").order("at", { ascending: false }).limit(300);
    qy = pick.startsWith("unmatched:") ? qy.is("device_id", null).eq("unique_id", pick.slice(10)) : qy.eq("device_id", pick);
    if (result) qy = qy.eq("result", result);
    const { data, error } = await qy;
    if (error) setErr(error.message); else setRows((data ?? []) as LogRow[]);
  }, [pick, result]);
  useEffect(() => { loadLog(); }, [loadLog]);
  useEffect(() => {
    if (!auto || !pick) return;
    const t = setInterval(loadLog, 10_000);
    return () => clearInterval(t);
  }, [auto, pick, loadLog]);

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    const order = { bad: 0, warn: 1, ok: 2, idle: 3 } as const;
    return (diag?.devices ?? []).filter((d) => (!company || (d.org ?? "") === company) && (!s || d.name.toLowerCase().includes(s) || d.unique_id.includes(s) || d.org?.toLowerCase().includes(s)))
      .sort((a, b) => order[a.diagnosis.level] - order[b.diagnosis.level] || a.name.localeCompare(b.name));
  }, [diag, q, company]);
  const devOpts = useMemo(() => {
    const s2 = devQ.trim().toLowerCase();
    const all = [...(diag?.devices ?? [])].filter((d) => !company || (d.org ?? "") === company).sort((a, b) => a.name.localeCompare(b.name));
    const hit = all.filter((d) => !s2 || d.name.toLowerCase().includes(s2) || d.unique_id.includes(s2) || d.org?.toLowerCase().includes(s2));
    const cur = (diag?.devices ?? []).find((d) => d.id === pick);
    return cur && !hit.includes(cur) ? [cur, ...hit] : hit;   // keep the chosen device listed
  }, [diag, devQ, pick, company]);
  const companies = useMemo(() => [...new Set((diag?.devices ?? []).map((d) => d.org ?? ""))].sort((a, b) => (a || "~").localeCompare(b || "~")), [diag]);
  // with a search that matches exactly one device, pick it straight away
  useEffect(() => { if (devQ.trim() && devOpts.length === 1 && devOpts[0].id !== pick) setPick(devOpts[0].id); }, [devQ, devOpts, pick]);
  const pickFromTable = (id: string) => { setPick(id); logRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const picked = diag?.devices?.find((d) => d.id === pick) ?? null;
  const counts = (diag?.devices ?? []).reduce((a, d) => ((a[d.diagnosis.level] += 1), a), { ok: 0, warn: 0, bad: 0, idle: 0 } as Record<Diag["level"], number>);

  function exportCsv() {
    const head = ["received_fiji", "kind", "result", "fix_time_fiji", "lat", "lng", "speed_kmh", "valid", "satellites", "protocol", "detail"];
    const esc = (v: unknown) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const body = rows.map((r) => [fiji(r.at, true), r.kind, r.result, fiji(r.fix_at, true), r.lat, r.lng, r.speed_kmh, r.valid, r.sat, r.protocol, r.detail].map(esc).join(","));
    const blob = new Blob(["﻿" + [head.join(","), ...body].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `TrackingFiji_Device-log_${(picked?.name ?? pick.replace("unmatched:", "unknown-")).replace(/[^\w-]+/g, "-")}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  }

  return (
    <div className="logs">
      <Toolbar>
        <h1>Device logs</h1>
        <input className="asearch" placeholder="Filter the health table" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="sub">{diag ? `Checked ${fiji(diag.at, true)}` : ""}</span>
        <button className="btn-primary" onClick={loadDiag} disabled={busy}>{busy ? "Checking…" : "Refresh"}</button>
      </Toolbar>
      <p className="logs-intro">Use this when a tracker should be working but isn't showing. <b>Traccar</b> is the tracking server the tracker connects to;
        the <b>platform</b> is this website. If Traccar hears the tracker but the platform doesn't, it's a platform problem. If Traccar doesn't hear it, look at the tracker (power, SIM, APN, server settings).</p>
      {err && <Notice kind="bad" onClose={() => setErr(null)}>{err}</Notice>}

      {diag && <div className="logs-tiles">
        <div className="tile bad"><b>{counts.bad}</b><span>Problem</span></div>
        <div className="tile warn"><b>{counts.warn}</b><span>Check</span></div>
        <div className="tile ok"><b>{counts.ok}</b><span>Reporting normally</span></div>
        <div className="tile"><b>{diag.unmatched.length}</b><span>Unknown trackers</span></div>
      </div>}

      <section className="logs-log top" ref={logRef}>
        <div className="logs-log-head">
          <h2>Message log</h2>
          <select value={company} onChange={(e) => setCompany(e.target.value)} aria-label="Company">
            <option value="">All companies</option>
            {companies.map((c) => <option key={c || "none"} value={c}>{c || "No company"}</option>)}
          </select>
          <input className="asearch" placeholder="Find a device (name, ID, company)" value={devQ} onChange={(e) => setDevQ(e.target.value)} aria-label="Find a device" />
          <select value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Device">
            <option value="">Choose a device…</option>
            {devOpts.map((d) => <option key={d.id} value={d.id}>{d.name} ({d.unique_id})</option>)}
            {(diag?.unmatched ?? []).filter((u) => !devQ.trim() || u.unique_id.includes(devQ.trim())).map((u) => <option key={u.unique_id} value={`unmatched:${u.unique_id}`}>Unknown · {u.unique_id}</option>)}
          </select>
          <select value={result} onChange={(e) => setResult(e.target.value)} aria-label="Result">
            <option value="">All results</option>
            {Object.entries(RESULT_LABEL).filter(([k]) => k !== "suspect").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label className="check"><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> Live (every 10 s)</label>
          <button className="btn-ghost" onClick={loadLog} disabled={!pick}>Reload</button>
          <button className="btn-ghost" onClick={exportCsv} disabled={!rows.length}>Export CSV</button>
        </div>
        {picked && <p className={`logs-diag ${picked.diagnosis.level}`}><b>{picked.name}:</b> {picked.diagnosis.text}</p>}
        {pick && (
          <div className="atable-wrap">
            <table className="atable logs-table">
              <thead><tr><th>Received (Fiji)</th><th>Type</th><th>Result</th><th>Fix time</th><th>Position</th><th className="num">Speed</th><th className="num">Sats</th><th>Detail</th></tr></thead>
              <tbody>
                {rows.map((r) => (<Fragment key={r.id}>
                  <tr onClick={() => setOpen(open === r.id ? null : r.id)} className="clickable">
                    <td className="mono">{fiji(r.at, true)}</td>
                    <td>{r.kind}</td>
                    <td><span className={`lvl ${RESULT_TONE[r.result] ?? "idle"}`}>{RESULT_LABEL[r.result] ?? r.result}</span></td>
                    <td className="mono">{fiji(r.fix_at, true)}{r.fix_at && Date.parse(r.at) - Date.parse(r.fix_at) > 10 * 60_000 && <div className="sub">delayed {ago(r.fix_at, Date.parse(r.at)).replace(" ago", "")}</div>}</td>
                    <td className="mono">{r.lat != null ? `${r.lat.toFixed(5)}, ${r.lng!.toFixed(5)}` : "—"}{r.valid === false && <div className="sub">valid = false</div>}</td>
                    <td className="num mono">{r.speed_kmh ?? "—"}</td>
                    <td className="num mono">{r.sat ?? "—"}</td>
                    <td className="small">{r.detail ?? ""}</td>
                  </tr>
                  {open === r.id && <tr className="logs-attrs"><td colSpan={8}><pre>{JSON.stringify({ protocol: r.protocol, unique_id: r.unique_id, ...r.attrs }, null, 2)}</pre></td></tr>}
                </Fragment>))}
                {!rows.length && <tr><td colSpan={8} className="sub">No messages in the last 14 days{result ? " with this result" : ""}. If Traccar shows recent contact, check Traccar's forwarding to the platform. For a client company's tracker, the message log only shows during a Support view of that company.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        <p className="sub">Messages are kept for 14 days. Click a row to see every attribute the tracker sent.</p>
      </section>

      <h2 className="logs-h2">Device health</h2>
      <div className="atable-wrap">
        <table className="atable logs-health">
          <thead><tr>
            <th>Device</th><th>Traccar</th><th>Last contact (Traccar)</th><th>Last message (platform)</th><th>Last fix</th><th className="num">Msgs 24 h</th><th>Diagnosis</th>
          </tr></thead>
          <tbody>
            {list.map((d) => (
              <tr key={d.id} className={pick === d.id ? "sel" : ""} onClick={() => pickFromTable(d.id)}>
                <td><b>{d.name}</b><div className="sub mono">{d.unique_id}{d.org ? ` · ${d.org}` : ""}</div></td>
                <td>{d.traccar ? <span className={`dot-label ${d.traccar.status === "online" ? "ok" : d.traccar.status === "offline" ? "bad" : "idle"}`}>{d.traccar.status ?? "unknown"}</span> : <span className="dot-label bad">not on server</span>}
                  {d.traccar?.protocol && <div className="sub mono">{d.traccar.protocol}</div>}</td>
                <td className="mono">{ago(d.traccar?.last_update, now)}<div className="sub">{fiji(d.traccar?.last_update)}</div></td>
                <td className="mono">{ago(d.platform.last_message, now)}<div className="sub">{d.platform.last_result ? RESULT_LABEL[d.platform.last_result] ?? d.platform.last_result : fiji(d.platform.last_message)}</div></td>
                <td className="mono">{fiji(d.platform.last_fix ?? d.traccar?.last_fix)}{d.traccar?.valid === false && <div className="sub">no fix at Traccar</div>}</td>
                <td className="num mono">{d.platform.messages_24h}</td>
                <td><span className={`lvl ${d.diagnosis.level}`}>{LEVEL_LABEL[d.diagnosis.level]}</span> <span className="diag">{d.diagnosis.text}</span></td>
              </tr>
            ))}
            {diag && !list.length && <tr><td colSpan={7} className="sub">No devices match.</td></tr>}
            {!diag && !err && <tr><td colSpan={7} className="sub">Checking devices…</td></tr>}
          </tbody>
        </table>
      </div>

      {diag && diag.unmatched.length > 0 && (
        <details className="logs-unmatched">
          <summary>{diag.unmatched.length} unknown tracker{diag.unmatched.length === 1 ? "" : "s"} sending to the server (not registered on the platform)</summary>
          <ul>{diag.unmatched.map((u) => (
            <li key={u.unique_id}><button className="btn-ghost small" onClick={() => pickFromTable(`unmatched:${u.unique_id}`)}>View log</button>
              <span className="mono">{u.unique_id}</span> · {u.hits} message{u.hits === 1 ? "" : "s"} · last {ago(u.last_seen, now)}
              <span className="sub"> — register it in Admin → Devices</span></li>
          ))}</ul>
        </details>
      )}

    </div>
  );
}
